import { isBefore, subMinutes } from 'date-fns'

import { sleep } from '@last/core'

import { printersApi } from '@/api/client'
import { logger } from '@/monitoring'
import sync from '@/sync/service'

import AdyenPrinter from './printers/adyen'
import Browser from './printers/browser'
import EscPos from './printers/escpos'
import KDS from './printers/kds'
import StarGraphic from './printers/star-graphic'
import StarPrnt from './printers/star-prnt'
import UsbPrinter from './printers/usb'
import UsbAndroid from './printers/usb-android'
import ZPL from './printers/zpl'
import type { Printer, PrinterConfig, PrinterDriver, PrinterJob } from './types'

const MAX_RETRIES = 24 // 2 minutes of retries

function getPrinter(printer: Printer): PrinterDriver {
  const config: PrinterConfig = {
    name: printer.name,
    ip: printer.ip!,
    type: printer.type,
    metadata: printer.metadata,
    ticketWidth: printer.ticketWidth,
    ticketHeight: printer.ticketHeight,
    ticketPaddingLeft: printer.ticketPaddingLeft
  }

  switch (printer.type) {
    case 'StarPRNT':
      return new StarPrnt(config)
    case 'StarGraphic':
      return new StarGraphic(config)
    case 'EscPos':
      return new EscPos(config)
    case 'EscPosMatrix':
      return new EscPos({ ...config, matrixMode: true })
    case 'AdyenPrinter':
      return new AdyenPrinter(config)
    case 'ZPL':
      return new ZPL(config)
    case 'Usb':
      return new UsbPrinter(config)
    case 'UsbAndroid':
      return new UsbAndroid(config)
    case 'KDS':
      return new KDS()
    default:
      return new Browser()
  }
}

async function print(queue: PrinterQueue) {
  if (queue.printing) return

  queue.printing = true

  while (queue.jobs.length > 0) {
    const job = queue.jobs[0]

    let jobDone = false

    try {
      if (job.time && isBefore(new Date(job.time), subMinutes(new Date(), 10))) {
        job.retries = MAX_RETRIES
        throw 'Job is too old'
      }

      queue.lastConnection = new Date()

      if (job.type === 'printImage') {
        await queue.printer.printImage(job.image)
      }

      if (job.type === 'openCashDrawer') {
        await queue.printer.openCashDrawer()
      }

      if (job.type === 'info') {
        const info = await queue.printer.getInfo?.()
        if (info) {
          printersApi
            .updatePrinter({
              id: queue.printerConfig.id,
              updatePrinterRequest: {
                model: `${info.makerName} - ${info.modelName} - ${info.firmwareVersion} - ${info.serialNumber}`
              }
            })
            .catch()
        }
      }

      jobDone = true
    } catch (error) {
      if (!job.retries || job.retries === 0) {
        logger.warn('Printer queue error', { error })
      }

      queue.hasError = true
      queue.errorMessage = formatError(error)

      await sleep(5_000)

      if (job.type !== 'openCashDrawer' && (!job.retries || job.retries < MAX_RETRIES)) {
        queue.jobs.push({ ...job, retries: (job.retries || 0) + 1 })
      }
    }

    if (job.printedEvent && jobDone) {
      sync.record(job.printedEvent.name, {
        ...job.printedEvent.data,
        printedTime: new Date()
      })
    }

    if (jobDone) {
      queue.hasError = false
      queue.errorMessage = null
    }

    queue.jobs.shift()
  }
  queue.printing = false
}

function formatError(err: unknown) {
  if (err instanceof Error) {
    return `${err.name}: ${err.message}`
  }

  try {
    return JSON.stringify(err)
  } catch {
    return String(err)
  }
}

class PrinterQueue {
  public jobs: PrinterJob[] = []
  public printer: PrinterDriver
  public printerConfig: Printer

  public printing = false
  public hasError = false
  public errorMessage: string | null = null
  public lastConnection: Date | null = null

  constructor(printer: Printer) {
    this.printer = getPrinter(printer)
    this.printerConfig = printer
    this.jobs = []
  }

  addJob(job: PrinterJob) {
    this.jobs.push(job)
    this.obtainPrinterInfo()
    print(this)
  }

  obtainPrinterInfo() {
    if (this.printer.getInfo && !this.printerConfig.model) {
      this.jobs.push({
        type: 'info'
      })
    }
  }
}

export default PrinterQueue
